/**
 * Simple Product Carousel - Generic product carousel component
 * (Replaces the removed Nosto-specific carousel with a generic implementation)
 */
import { useEffect, useRef, useState } from 'preact/hooks'
import CarouselSwiper from 'modules/carousel-swiper/carousel-swiper'
import PlpCard from 'snippets/plp-card/plp-card'
import { getPrice, getTags, checkItemInCart } from 'libs/searchspring/searchspring-utils'
import { emitterProps } from 'helpers/emitter-event'

export default function ProductCarouselSimple({
  products,
  scrollbar = true,
  navigation = false,
  centerSlides = false,
  options = {},
  smallMode = false,
  className = ''
}) {
  if (!products || products.length === 0) {
    return null
  }

  const swiperInstanceRef = useRef(null)
  const [swiperReady, setSwiperReady] = useState(false)

  const handleSetInstance = (instance) => {
    swiperInstanceRef.current = instance
    if (instance) {
      setSwiperReady(true)
    }
  }

  const defaultOptions = {
    slidesPerView: smallMode ? 2.8 : 'auto',
    spaceBetween: 16,
    freeMode: {
      enabled: true,
      sticky: true
    },
    pagination: {
      clickable: true
    },
    scrollbar: {
      draggable: true
    },
    centerInsufficientSlides: centerSlides,
    grabCursor: true,
    speed: 500,
    breakpoints: {
      768: {
        slidesPerView: smallMode ? 2.8 : 3,
        spaceBetween: 24
      },
      1024: {
        slidesPerView: smallMode ? 2.8 : 4,
        spaceBetween: 24
      },
      1536: {
        slidesPerView: smallMode ? 2.8 : 5,
        spaceBetween: 24
      }
    },
    ...options
  }
  const productItems = products.map((product) => {
    if (!product) return null

    // getPrice() converts SearchSpring dollars to Shopify cents
    const price = typeof product.price === 'number' ? getPrice(product.price) : getPrice(parseFloat(product.price) || 0)
    const originalPrice = product.compareAtPrice ? getPrice(product.compareAtPrice) : (product.compareAtPriceMin ? getPrice(product.compareAtPriceMin) : null)

    return (
      <swiper-slide key={product.id} className="w-[264px] lg:w-1/4">
        <PlpCard
          id={product.id}
          title={product.title}
          subtitle={product.sizes}
          handle={product.handle}
          imageUrl={product.featuredImage || ''}
          price={price}
          originalPrice={originalPrice}
          tags={product.tags ? getTags(product.tags) : []}
          isInCart={product.uid ? checkItemInCart(product.uid) : false}
          showSwatches={true}
          linkedColors={product.linkedColors}
          currentSize={product.sizes}
          sku={product.sku}
          ratioImage="aspect-[1/1] overflow-hidden"
          className="plp-card-carousel flex flex-col flex-shrink-0 h-full"
          variants={product.variants}
          isCartRelated={true}
        />
      </swiper-slide>
    )
  })

  useEffect(() => {
    if (!swiperReady || !swiperInstanceRef.current) return
    swiperInstanceRef.current.update?.()
  }, [swiperReady, products])

  useEffect(() => {
    if (!swiperReady || !swiperInstanceRef.current) return
    if (typeof window === 'undefined' || typeof document === 'undefined' || typeof MutationObserver === 'undefined') return

    const updateOnOpen = () => {
      if (document.body.classList.contains('mini-cart-open')) {
        requestAnimationFrame(() => {
          swiperInstanceRef.current?.update?.()
        })
      }
    }

    updateOnOpen()

    const observer = new MutationObserver(updateOnOpen)
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] })

    return () => {
      observer.disconnect()
    }
  }, [swiperReady])

  useEffect(() => {
    if (!swiperReady || !swiperInstanceRef.current) return
    if (typeof document === 'undefined') return

    const bumpSwiper = () => {
      requestAnimationFrame(() => {
        swiperInstanceRef.current?.update?.()
        swiperInstanceRef.current?.updateSlides?.()
        swiperInstanceRef.current?.updateProgress?.()
        swiperInstanceRef.current?.updateSize?.()
      })
    }

    document.addEventListener(emitterProps.cart.render, bumpSwiper)
    document.addEventListener(emitterProps.cart.update, bumpSwiper)

    return () => {
      document.removeEventListener(emitterProps.cart.render, bumpSwiper)
      document.removeEventListener(emitterProps.cart.update, bumpSwiper)
    }
  }, [swiperReady])

  useEffect(() => {
    if (!swiperReady || !swiperInstanceRef.current?.el) return
    if (typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        swiperInstanceRef.current?.update?.()
        swiperInstanceRef.current?.updateSize?.()
        swiperInstanceRef.current?.updateSlides?.()
      })
    })

    observer.observe(swiperInstanceRef.current.el)

    return () => {
      observer.disconnect()
    }
  }, [swiperReady])

  useEffect(() => {
    if (!swiperReady) return
    if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') return

    const modalEl = document.querySelector('modal-dialog[data-target="MiniCart"]')
    if (!modalEl) {
      return
    }

    const handleModalState = () => {
      const isOpen = modalEl.classList.contains('modal-dialog-opened') || modalEl.hasAttribute('open')
      if (!isOpen) return

      requestAnimationFrame(() => {
        swiperInstanceRef.current?.update?.()
        swiperInstanceRef.current?.updateSize?.()
        swiperInstanceRef.current?.updateSlides?.()
      })
    }

    handleModalState()

    const observer = new MutationObserver(handleModalState)
    observer.observe(modalEl, { attributes: true, attributeFilter: ['class', 'open'] })

    return () => {
      observer.disconnect()
    }
  }, [swiperReady])

  return (
    <div className={className}>
      <CarouselSwiper
        items={productItems}
        options={defaultOptions}
        navigation={navigation}
        scrollbar={scrollbar}
        className="product-carousel-swiper"
        setCarouselInstance={handleSetInstance}
      />
    </div>
  )
}
